import type { LucideIcon } from "lucide-react";

import { AccountTagEnum } from "@nlc/shared-types";
import {
  Pause,
  Star,
  Sunset as SunsetIcon,
  Tag,
  User,
  Wrench,
  X,
} from "lucide-react";

import pendingCancellationIcon from "@/assets/pending_cancellation_icon.svg";

export interface AccountTagConfig {
  bgColor: string;
  color: string;
  icon?: LucideIcon;
  iconSrc?: string;
  label: string;
  priority: number;
  tooltip: string;
  type: AccountTagEnum;
}

/**
 * Tag configurations mapping
 */
export const ACCOUNT_TAG_CONFIGS: Record<AccountTagEnum, AccountTagConfig> = {
  [AccountTagEnum.CANCELLED]: {
    bgColor: "bg-red-50",
    color: "text-red-600",
    icon: X,
    label: "Cancelled",
    priority: 2,
    tooltip: "Cancelled",
    type: AccountTagEnum.CANCELLED,
  },
  [AccountTagEnum.CONCIERGE]: {
    bgColor: "bg-green-50",
    color: "text-green-600",
    icon: User,
    label: "Concierge",
    priority: 6,
    tooltip: "Concierge",
    type: AccountTagEnum.CONCIERGE,
  },
  [AccountTagEnum.MAINTENANCE]: {
    bgColor: "bg-pink-50",
    color: "text-pink-600",
    icon: Wrench,
    label: "Maintenance",
    priority: 5,
    tooltip: "Maintenance",
    type: AccountTagEnum.MAINTENANCE,
  },
  [AccountTagEnum.NEW]: {
    bgColor: "bg-blue-50",
    color: "text-blue-600",
    icon: Star,
    label: "New",
    priority: 4,
    tooltip: "New",
    type: AccountTagEnum.NEW,
  },
  [AccountTagEnum.PAUSED]: {
    bgColor: "bg-orange-50",
    color: "text-orange-600",
    icon: Pause,
    label: "Paused",
    priority: 3,
    tooltip: "Pause",
    type: AccountTagEnum.PAUSED,
  },
  [AccountTagEnum.PENDING_CANCELLATION]: {
    bgColor: "bg-red-50",
    color: "text-red-600",
    iconSrc: pendingCancellationIcon,
    label: "Pending Cancellation",
    priority: 7,
    tooltip: "Pending cancellation",
    type: AccountTagEnum.PENDING_CANCELLATION,
  },
  [AccountTagEnum.SUNSET]: {
    bgColor: "bg-orange-50",
    color: "text-orange-600",
    icon: SunsetIcon,
    label: "Sunset",
    priority: 1,
    tooltip: "Sunset Shift",
    type: AccountTagEnum.SUNSET,
  },
  [AccountTagEnum.WHITELABEL_CHILD]: {
    bgColor: "bg-bg-primary",
    color: "text-text-secondary",
    icon: Tag,
    label: "Whitelabel - Child",
    priority: 9,
    tooltip: "Whitelabel - Child",
    type: AccountTagEnum.WHITELABEL_CHILD,
  },
  [AccountTagEnum.WHITELABEL_PARENT]: {
    bgColor: "bg-bg-primary",
    color: "text-text-secondary",
    icon: Tag,
    label: "Whitelabel - Parent",
    priority: 8,
    tooltip: "Whitelabel - Parent",
    type: AccountTagEnum.WHITELABEL_PARENT,
  },
};

export function hasWhitelabelTag(tags: AccountTagEnum[] | undefined): boolean {
  return Boolean(
    tags?.some(
      (tag) =>
        tag === AccountTagEnum.WHITELABEL_PARENT ||
        tag === AccountTagEnum.WHITELABEL_CHILD,
    ),
  );
}

export function whitelabelRowClassName(row: {
  account?: { tags?: AccountTagEnum[] };
}): string {
  return hasWhitelabelTag(row.account?.tags) ? "!bg-utility-purple-50" : "";
}

/**
 * Get sorted tag configurations based on priority
 */
export function getSortedTags(
  tagConfigs: AccountTagConfig[],
): AccountTagConfig[] {
  return tagConfigs.sort((a, b) => a.priority - b.priority);
}

/**
 * Get active tag configurations from account tags array (from API)
 * @param tags - Array of AccountTagEnum values from the backend
 * @returns Sorted array of tag configurations to display
 */
export function getActiveTagConfigs(
  tags: AccountTagEnum[],
): AccountTagConfig[] {
  const activeConfigs: AccountTagConfig[] = [];

  for (const tag of tags) {
    const config = ACCOUNT_TAG_CONFIGS[tag];

    if (config) {
      activeConfigs.push(config);
    }
  }

  return getSortedTags(activeConfigs);
}
